"use client"

import { useState, useEffect, useContext, useMemo } from "react"
import { Box, Typography, TextField, Button, Modal, InputAdornment, Divider } from "@mui/material"
import { PointOfSale as PointOfSaleIcon, CurrencyExchange as CurrencyExchangeIcon } from "@mui/icons-material"
import { ErpContext } from "../../context/ErpContext.js"
import { formatInputWithSeparators, parseLocalizedNumber, getLocaleConfig } from "../../utils/numeros.js"

// Función helper para redondear a 2 decimales (operaciones monetarias)
const roundToTwoDecimals = (value) => {
  return Math.round(value * 100) / 100
}

// Mantener alias para compatibilidad con código existente
const truncateToTwoDecimals = roundToTwoDecimals

const CashPaymentModal = ({ open, onClose, totalAmount, onConfirm }) => {
  const { formatoNumericoGlobal } = useContext(ErpContext)
  const [amountPaidDisplay, setAmountPaidDisplay] = useState("")
  
  // Obtener configuración de locale (memoizado para evitar recálculos)
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )
  
  // Truncar totalAmount a 2 decimales para evitar problemas de precisión
  const totalAmountTruncated = useMemo(() => {
    return truncateToTwoDecimals(totalAmount || 0)
  }, [totalAmount])
  
  // Update amountPaidDisplay when totalAmount changes
  useEffect(() => {
    if (totalAmountTruncated && totalAmountTruncated > 0) {
      const formatted = formatInputWithSeparators(totalAmountTruncated, localeConfig.locale)
      setAmountPaidDisplay(formatted)
    } else {
      setAmountPaidDisplay("")
    }
  }, [totalAmountTruncated, localeConfig])
  
  // Truncar el monto recibido a 2 decimales
  const numericAmount = truncateToTwoDecimals(parseLocalizedNumber(amountPaidDisplay, localeConfig.locale) || 0)
  // Truncar el cambio a 2 decimales
  const change = truncateToTwoDecimals(numericAmount - totalAmountTruncated)

  const handleConfirm = () => {
    if (numericAmount >= totalAmountTruncated) {
      onConfirm(numericAmount)         
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(amount)
  }

  // Handle Enter key press to confirm payment
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && numericAmount >= totalAmountTruncated) {
      e.preventDefault() // Prevenir que el navegador dispare automáticamente el botón
      handleConfirm()
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
          <PointOfSaleIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
          Pago en Efectivo
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Total a pagar:
          </Typography>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
            {formatCurrency(totalAmountTruncated)}
          </Typography>

          <TextField
            label="Monto recibido"
            fullWidth
            value={amountPaidDisplay}
            onChange={(e) => {
              const inputValue = e.target.value
              const { locale, decimalSeparator } = localeConfig

              if (inputValue === "") {
                setAmountPaidDisplay("")
                return
              }

              const parsedValue = parseLocalizedNumber(inputValue, locale)

              if (parsedValue === null && inputValue.trim() !== "") {
                return // Keep previous valid input if current is unparseable
              }

              let formattedValue = parsedValue !== null
                ? formatInputWithSeparators(parsedValue, locale)
                : ""

              const hasDecimalSeparator = inputValue.includes(decimalSeparator)
              const endsWithDecimal = inputValue.endsWith(decimalSeparator)
              const [, fractionalPartOriginal] = inputValue.split(decimalSeparator)

              // Special handling for decimal separator at the end or during fractional input
              if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
                formattedValue += decimalSeparator
              } else if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
                const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6)
                const integerPart = formattedValue.split(decimalSeparator)[0]
                formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`
              }

              setAmountPaidDisplay(formattedValue)

              // Keep cursor at the end
              requestAnimationFrame(() => {
                const input = e.target
                if (input) {
                  input.setSelectionRange(formattedValue.length, formattedValue.length)
                }
              })
            }}
            onBlur={(e) => {
              const { locale } = localeConfig
              const parsed = parseLocalizedNumber(e.target.value, locale)
              if (parsed !== null && parsed >= 0) {
                const formatted = formatInputWithSeparators(parsed, locale)
                setAmountPaidDisplay(formatted)
              } else if (e.target.value === "") {
                setAmountPaidDisplay("")
              }
            }}
            onKeyDown={handleKeyDown}
            type="text"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{
              inputMode: "decimal",
              autoComplete: "off",
            }}
            autoFocus
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ display: "flex", alignItems: "center" }}>
            <CurrencyExchangeIcon sx={{ mr: 1, color: change >= 0 ? "green" : "red" }} />
            Cambio a devolver:
          </Typography>
          <Typography variant="h5" fontWeight="bold" color={change >= 0 ? "green" : "red"}>
            {formatCurrency(change >= 0 ? change : 0)}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={numericAmount < totalAmountTruncated}
            sx={{ bgcolor: "green" }}
          >
            Confirmar
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default CashPaymentModal
